import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency, num, dateShort } from "@/lib/format";
import { RunBadge, SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PackageCheck, ChevronRight } from "lucide-react";

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [skuMap, setSkuMap] = useState({});
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/receipts").then((r) => setReceipts(r.data));
    api.get("/skus").then((r) => setSkuMap(Object.fromEntries(r.data.map((s) => [s.sku_code, s]))));
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Purchase Receipts" subtitle="Incoming stock from vendors and receiving QA status" />

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">PO / Receipt #</th>
              <th className="px-4 py-3 text-left">Date Received</th>
              <th className="px-4 py-3 text-left">Vendor</th>
              <th className="px-4 py-3 text-left">Destination</th>
              <th className="px-4 py-3 text-right">Items</th>
              <th className="px-4 py-3 text-right">Total Qty</th>
              <th className="px-4 py-3 text-right">Landed Cost</th>
              <th className="px-4 py-3 text-left">QA Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {receipts.map((r) => (
              <tr key={r.receipt_no} data-testid={`receipt-row-${r.receipt_no}`} onClick={() => setActive(r)}
                className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 cursor-pointer transition-colors">
                <td className="px-4 py-3"><SkuTag code={r.receipt_no} /></td>
                <td className="px-4 py-3 text-stone-600">{dateShort(r.date)}</td>
                <td className="px-4 py-3 font-medium text-stone-800">{r.vendor}</td>
                <td className="px-4 py-3 text-stone-600">{r.facility_name}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{r.items_count}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-700">{num(r.total_qty)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-stone-900">{currency(r.total_cost)}</td>
                <td className="px-4 py-3"><RunBadge status={r.qa_status} /></td>
                <td className="px-4 py-3 text-stone-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg bg-white">
          {active && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-emerald-600" />{active.receipt_no} · {active.vendor}</DialogTitle></DialogHeader>
              <div className="text-sm text-stone-500 border-b border-stone-100 pb-2">Received {dateShort(active.date)} at {active.facility_name}</div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <th className="px-2 py-2 text-left">Item</th><th className="px-2 py-2 text-right">Qty</th>
                  <th className="px-2 py-2 text-right">Unit Cost</th><th className="px-2 py-2 text-right">Line</th></tr></thead>
                <tbody>
                  {active.items.map((it) => (
                    <tr key={it.sku_code} className="border-b border-stone-50">
                      <td className="px-2 py-2"><div className="font-medium text-stone-800">{skuMap[it.sku_code]?.name || it.sku_code}</div><SkuTag code={it.sku_code} /></td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums">{num(it.qty)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums text-stone-600">{currency(it.cost)}</td>
                      <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold">{currency(it.qty * it.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
